import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { Loader2, Send, Paperclip, X, FileText } from "lucide-react";
import { PortalHeader, PortalEmpty } from "@/components/portal/PortalHeader";
import { getMyProject, listMyMessages, sendMyMessage, markMyMessagesRead, getMyFileSignedUrl } from "@/lib/portal/portal.functions";
import { supabase } from "@/integrations/supabase/client";

export const Route = createFileRoute("/_authenticated/portal/messages")({
  component: MessagesPage,
});

type Attachment = { url: string; name: string; mime?: string };

function MessagesPage() {
  const qc = useQueryClient();
  const projectFn = useServerFn(getMyProject);
  const listFn = useServerFn(listMyMessages);
  const sendFn = useServerFn(sendMyMessage);
  const markFn = useServerFn(markMyMessagesRead);

  const projectQ = useQuery({ queryKey: ["portal", "project"], queryFn: () => projectFn() });
  const projectId = projectQ.data?.project?.id;
  const messagesQ = useQuery({
    queryKey: ["portal", "messages", projectId],
    queryFn: () => listFn({ data: { projectId: projectId! } }),
    enabled: !!projectId,
    refetchInterval: 10000,
  });

  const [body, setBody] = useState("");
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [sending, setSending] = useState(false);
  const scrollerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!projectId) return;
    markFn({ data: { projectId } }).then(() => qc.invalidateQueries({ queryKey: ["portal", "project"] }));
  }, [projectId, messagesQ.data?.messages.length]);

  useEffect(() => {
    scrollerRef.current?.scrollTo({ top: scrollerRef.current.scrollHeight });
  }, [messagesQ.data?.messages.length]);

  if (projectQ.isLoading) return <div className="py-20 grid place-items-center"><Loader2 className="size-6 animate-spin text-amber-300" /></div>;
  if (!projectId) return <PortalEmpty title="No project yet" />;

  const send = async () => {
    if (!body.trim() && pendingFiles.length === 0) return;
    setSending(true);
    try {
      const attachments: Attachment[] = [];
      for (const file of pendingFiles) {
        const ext = file.name.split(".").pop()?.toLowerCase() || "bin";
        const path = `${projectId}/messages/${crypto.randomUUID()}.${ext}`;
        const { error } = await supabase.storage.from("project-files").upload(path, file, { contentType: file.type });
        if (error) throw error;
        attachments.push({ url: path, name: file.name, mime: file.type });
      }
      await sendFn({ data: { projectId, body: body.trim() || undefined, attachments: attachments.length ? attachments : undefined } });
      setBody(""); setPendingFiles([]);
      await qc.invalidateQueries({ queryKey: ["portal", "messages", projectId] });
    } catch (e: any) { alert(e?.message || "Failed to send"); }
    finally { setSending(false); }
  };

  const messages = messagesQ.data?.messages ?? [];
  const me = messagesQ.data?.me;

  return (
    <div className="flex flex-col h-[calc(100vh-7rem)] lg:h-[calc(100vh-5rem)]">
      <PortalHeader eyebrow="Messages" title="Chat with your team" description="Direct line to your project manager and painter. Replies typically within a business day." />
      <div ref={scrollerRef} className="flex-1 overflow-y-auto rounded-2xl border border-white/10 bg-black/30 p-4 space-y-3">
        {messagesQ.isLoading ? (
          <div className="py-12 grid place-items-center"><Loader2 className="size-5 animate-spin text-amber-300" /></div>
        ) : messages.length === 0 ? (
          <p className="text-center text-sm text-zinc-500 py-12">No messages yet. Say hi 👋</p>
        ) : messages.map((m) => {
          const mine = m.sender_user_id === me;
          return (
            <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm ${
                mine ? "bg-amber-300 text-zinc-950" : "bg-white/[0.06] text-zinc-100 border border-white/10"
              }`}>
                {!mine && <p className="text-[10px] uppercase tracking-wider text-amber-300 mb-1 font-semibold">{m.sender_name ?? "Team"}</p>}
                {m.body && <p className="whitespace-pre-wrap leading-relaxed">{m.body}</p>}
                {Array.isArray(m.attachments) && m.attachments.length > 0 && (
                  <div className="mt-2 space-y-1">
                    {m.attachments.map((a: any, i: number) => <MessageAttachment key={i} projectId={projectId} att={a} mine={mine} />)}
                  </div>
                )}
                <p className={`text-[10px] mt-1 ${mine ? "text-zinc-700" : "text-zinc-500"}`}>{new Date(m.created_at).toLocaleString([], { hour: "numeric", minute: "2-digit", month: "short", day: "numeric" })}</p>
              </div>
            </div>
          );
        })}
      </div>

      {pendingFiles.length > 0 && (
        <div className="flex gap-2 flex-wrap mt-2">
          {pendingFiles.map((f, i) => (
            <span key={i} className="inline-flex items-center gap-1.5 bg-white/5 border border-white/10 rounded-full px-3 py-1 text-[11px] text-zinc-300">
              {f.name}
              <button onClick={() => setPendingFiles(pendingFiles.filter((_, j) => j !== i))}><X className="size-3" /></button>
            </span>
          ))}
        </div>
      )}

      <div className="mt-3 flex items-end gap-2 rounded-2xl border border-white/10 bg-white/[0.02] p-2">
        <label className="size-10 rounded-xl bg-white/5 hover:bg-white/10 grid place-items-center cursor-pointer shrink-0">
          <Paperclip className="size-4 text-zinc-300" />
          <input type="file" multiple accept="image/*,application/pdf" className="hidden"
            onChange={(e) => setPendingFiles([...pendingFiles, ...Array.from(e.target.files ?? [])])} />
        </label>
        <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={1} placeholder="Type a message…"
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
          className="flex-1 resize-none bg-transparent text-base text-white placeholder:text-zinc-500 px-3 py-2 focus:outline-none max-h-32" />
        <button onClick={send} disabled={sending || (!body.trim() && pendingFiles.length === 0)}
          className="size-10 rounded-xl bg-amber-300 hover:bg-amber-200 disabled:opacity-40 grid place-items-center text-zinc-950 shrink-0">
          {sending ? <Loader2 className="size-4 animate-spin" /> : <Send className="size-4" />}
        </button>
      </div>
    </div>
  );
}

function MessageAttachment({ projectId, att, mine }: { projectId: string; att: any; mine: boolean }) {
  const isHttp = /^https?:\/\//i.test(att.url);
  const signFn = useServerFn(getMyFileSignedUrl);
  const q = useQuery({
    queryKey: ["portal", "sign", projectId, att.url],
    queryFn: () => signFn({ data: { projectId, path: att.url } }),
    enabled: !isHttp,
    staleTime: 4 * 60 * 1000,
  });
  const url = isHttp ? att.url : q.data?.url;
  const isImage = (att.mime ?? "").startsWith("image/");
  if (isImage && url) {
    return <a href={url} target="_blank" rel="noopener"><img src={url} alt={att.name} className="rounded-lg max-h-48 max-w-full" /></a>;
  }
  return (
    <a href={url ?? "#"} target="_blank" rel="noopener"
      className={`inline-flex items-center gap-2 text-xs underline ${mine ? "text-zinc-800" : "text-amber-200"}`}>
      <FileText className="size-3.5" /> {att.name}
    </a>
  );
}